import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { SelectProjectPrompt } from '@/components/projects/SelectProjectPrompt';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useProject } from '@/contexts/ProjectContext';
import { getDeliveryStatus, setLifecyclePhase, executeOutcome, getProjectMode } from '@/lib/api';
import type { CodebaseResponse, DeliveryActivityEntry } from '@/lib/api';
import { LifecycleStepper } from '@/components/delivery/LifecycleStepper';
import { BlockerCard } from '@/components/delivery/BlockerCard';
import { DependencyGraphCard } from '@/components/delivery/DependencyGraphCard';
import { OutcomeTable } from '@/components/delivery/OutcomeTable';
import { bucketByConvergence } from '@/lib/convergence-buckets';

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DeliveryPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProjectId, setSelectedProjectId, codebases, isLoadingCodebases } = useProject();
  const codebase = codebases?.find(cb => cb.id === selectedProjectId);

  const { data: delivery, isLoading } = useQuery({
    queryKey: ['delivery-status', selectedProjectId],
    queryFn: () => getDeliveryStatus(selectedProjectId ?? ''),
    enabled: !!selectedProjectId,
  });

  const { data: projectMode } = useQuery({
    queryKey: ['project-mode', selectedProjectId],
    queryFn: () => getProjectMode(selectedProjectId ?? ''),
    enabled: !!selectedProjectId,
  });

  const phaseMutation = useMutation({
    mutationFn: (phase: number) => setLifecyclePhase(selectedProjectId ?? '', phase),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-status', selectedProjectId] });
    },
  });

  const executeMutation = useMutation({
    mutationFn: (outcomeId: string) => executeOutcome(selectedProjectId ?? '', outcomeId),
    onSuccess: data => {
      void navigate(`/chat/${data.conversationId}`);
    },
  });

  const rollup = delivery?.rollup;
  const attentionCount = delivery?.attentionItems.length ?? 0;
  const buckets = delivery ? bucketByConvergence(delivery.outcomes) : null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Project Delivery" projectName={codebase?.name} />
      <PageBody contentClassName="space-y-6">
        {/* Project selector */}
        <div className="flex items-center gap-3">
          <label
            htmlFor="delivery-project-select"
            className="text-sm text-text-secondary whitespace-nowrap"
          >
            Project:
          </label>
          <select
            id="delivery-project-select"
            value={selectedProjectId ?? ''}
            onChange={(e): void => {
              setSelectedProjectId(e.target.value || null);
            }}
            className="h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring flex-1 max-w-md"
          >
            <option value="">Select a project...</option>
            {isLoadingCodebases ? (
              <option disabled>Loading...</option>
            ) : (
              codebases?.map((cb: CodebaseResponse) => (
                <option key={cb.id} value={cb.id}>
                  {cb.name}
                </option>
              ))
            )}
          </select>
        </div>

        {!selectedProjectId ? (
          <SelectProjectPrompt action="view delivery status" />
        ) : isLoading ? (
          <div className="text-sm text-muted-foreground">Loading delivery status...</div>
        ) : !delivery || delivery.outcomes.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="text-text-secondary text-sm">
                No outcomes yet. Create outcomes on the{' '}
                <button
                  onClick={() => {
                    void navigate('/outcomes');
                  }}
                  className="text-primary underline"
                >
                  Outcomes page
                </button>{' '}
                to start tracking delivery.
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Lifecycle stepper */}
            <LifecycleStepper
              currentPhase={delivery.lifecyclePhase?.current ?? null}
              mode={projectMode?.mode ?? null}
              onPhaseChange={phase => {
                phaseMutation.mutate(phase);
              }}
            />

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">
                    {rollup?.totalOutcomes ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Outcomes</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">{rollup?.active ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Active</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-error">{attentionCount}</div>
                  <div className="text-xs text-muted-foreground">Needs Attention</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">
                    {rollup?.averageConvergenceScore !== null &&
                    rollup?.averageConvergenceScore !== undefined
                      ? `${String(rollup.averageConvergenceScore)}%`
                      : '--'}
                  </div>
                  <div className="text-xs text-muted-foreground">Avg Convergence</div>
                </CardContent>
              </Card>
            </div>

            {/* Convergence distribution */}
            {buckets && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary">Convergence</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-error">{buckets.rework}</div>
                      <div className="text-xs text-muted-foreground">Rework</div>
                      <div className="text-xs text-text-tertiary">&lt;70%</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-warning">{buckets.review}</div>
                      <div className="text-xs text-muted-foreground">Manual Review</div>
                      <div className="text-xs text-text-tertiary">70–89%</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-success">{buckets.ready}</div>
                      <div className="text-xs text-muted-foreground">Ready to Ship</div>
                      <div className="text-xs text-text-tertiary">90%+</div>
                    </CardContent>
                  </Card>
                </div>
                {buckets.unscored > 0 && (
                  <div className="text-xs text-text-tertiary">
                    {buckets.unscored} outcome{buckets.unscored === 1 ? '' : 's'} not yet scored
                  </div>
                )}
              </div>
            )}

            {/* Blockers */}
            {attentionCount > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-error">
                  Needs Attention ({attentionCount})
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {delivery.attentionItems.map(item => (
                    <BlockerCard
                      key={item.outcome.id}
                      item={item}
                      onExecute={() => {
                        executeMutation.mutate(item.outcome.id);
                      }}
                      onView={() => {
                        void navigate(`/outcomes/${item.outcome.id}`);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <DependencyGraphCard
              outcomes={delivery.outcomes}
              onView={outcomeId => {
                void navigate(`/outcomes/${outcomeId}`);
              }}
            />

            {/* Outcome table */}
            <OutcomeTable
              outcomes={delivery.outcomes}
              attentionItems={delivery.attentionItems}
              onExecute={outcomeId => {
                executeMutation.mutate(outcomeId);
              }}
              onView={outcomeId => {
                void navigate(`/outcomes/${outcomeId}`);
              }}
            />

            {/* Recent activity */}
            {delivery.recentActivity.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative border-l border-border pl-4 space-y-3">
                    {delivery.recentActivity.map((entry: DeliveryActivityEntry, i: number) => (
                      <div
                        key={`${entry.outcomeId}-${entry.timestamp}-${String(i)}`}
                        className="relative"
                      >
                        <div className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                        <div className="text-xs text-text-tertiary">
                          {formatTimestamp(entry.timestamp)}
                        </div>
                        <div className="text-sm text-text-primary">
                          <span className="font-mono text-text-secondary text-xs mr-1">
                            {entry.outcomeId}
                          </span>
                          <span className="font-medium">{entry.action}</span>
                          <span className="text-text-secondary ml-2">by {entry.actor}</span>
                        </div>
                        {entry.details && (
                          <div className="mt-0.5 text-xs text-text-secondary">{entry.details}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </PageBody>
    </div>
  );
}
